import { useEffect, useState, useRef } from 'react';
import { Box, Typography, Tooltip, IconButton, TextField, Button, InputAdornment, SelectChangeEvent, CircularProgress, Skeleton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CheckIcon } from 'lucide-react';
import CustomIcons from '../../../assests/CustomIcons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { workspaceWidgetReportDataAction, workspaceWidgetReportExportAction, workspaceWidgetRetryScanAction } from '../../../redux/actions/AgencyAction';
import useDebounce from '../../../Hooks/useDebounce';
import moment from 'moment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Pagination, Select, MenuItem } from '@mui/material';
import MaterialIcons from '../../../assests/MaterialIcons/MaterialIcons';
import { menuItemSX } from '../../../components/Builder/BuilderConst';
import { CSVLink } from 'react-csv';
import { AGENCY_EXPORT_CSV_DATA, AGENCY_EXPORT_CSV_HEADER } from '../../../utils/constant';

import { formattedDate2 } from '../../../components/LocalSEOHeatmap/helpers';

interface AgencyWidgetProps {
    styles: any;
    widgetConfig: any;
    widgetStep: number;
    setWidgetStep: (val: number) => void;
    setIsDrawerOpen: (val: boolean) => void;
    handleCopy: () => void;
    copied: boolean;
    codeRef: React.RefObject<HTMLPreElement | null>;
    API_BASE_URL: string;
    workspaceWidgetData: any;
    widgetLoading: boolean;
}

const AgencyWidget: React.FC<AgencyWidgetProps> = ({
    styles,
    widgetConfig,
    widgetStep,
    setWidgetStep,
    setIsDrawerOpen,
    handleCopy,
    copied,
    codeRef,
    API_BASE_URL,
    workspaceWidgetData,
    widgetLoading
}) => {
    const widgetId = workspaceWidgetData?.data?.widget?._id;
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);
    const [sortConfig, setSortConfig] = useState<{
        key: string | null;
        direction: 'asc' | 'desc';
    }>({ key: 'createdAt', direction: 'desc' });

    const dispatch = useDispatch<AppDispatch>();
    const { workspaceWidgetReportData, workspaceWidgetReportDataLoading, workspaceWidgetReportExportData, workspaceWidgetReportExportLoading } = useSelector((state: RootState) => state.agency);
    const csvLinkRef = useRef<any>(null);

    const handleExportClick = async () => {
        const result = await dispatch(workspaceWidgetReportExportAction());
        if (workspaceWidgetReportExportAction.fulfilled.match(result)) {
            setTimeout(() => {
                csvLinkRef.current?.link?.click();
            }, 0);
        }
    };

    const searchDebounce = useDebounce(searchTerm, 500);

    const fetchReports = () => {
        dispatch(workspaceWidgetReportDataAction({
            page,
            keyword: searchDebounce,
            limit: rowsPerPage
        }));
    };

    useEffect(() => {
        fetchReports();
    }, [dispatch, searchDebounce, page, rowsPerPage]);

    // removed initial export download


    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
        setRowsPerPage(event?.target?.value as number);
        setPage(1);
    };

    const getSortArrow = (key: string) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ? <MaterialIcons.ArrowDropUp className="arrow-up-animation" /> : <MaterialIcons.ArrowDropUp className="arrow-down-animation" />;
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
            return { key, direction };
        });
    };

    const reports = workspaceWidgetReportData?.data || [];
    const pagination = workspaceWidgetReportData?.pagination || {};

    const sortedData = [...reports].sort((a: any, b: any) => {
        if (!sortConfig.key) return 0;
        const { key, direction } = sortConfig;
        let valA = a[key];
        let valB = b[key];
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();
        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
    });

    const getStatusStyles = (status?: string) => {
        const s = status?.toLowerCase();
        switch (s) {
            case 'draft':
                return { bg: '#FFF6D8', color: '#FFCC01' };
            case 'pending':
            case 'waiting_for_approval':
                return { bg: '#FFE6D4', color: '#FF6600' };
            case 'failed':
            case 'skipped':
                return { bg: '#FDE2DF', color: '#EA4136' };
            case 'scheduled':
                return { bg: '#F9D9FB', color: '#CE10E0' };
            case 'published':
            case 'completed':
                return { bg: '#E7F5DB', color: '#5F9B20' };
            default:
                return { bg: '#E8F0FE', color: '#1A73E8' };
        }
    };

    return (
        <Box sx={styles.sectionContainer}>
            <Typography sx={styles.sectionTitle}>Ranking Report Widget</Typography>
            <Typography sx={{ ...styles.typography, color: '#6b7280', fontSize: '14px', mb: 3 }}>
                Create, install, and manage your ranking report widget.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
                {/* LEFT SIDE */}
                <Box sx={{ flex: 1, width: '100%' }}>
                    <Box sx={{
                        backgroundColor: widgetConfig.widget_background_color,
                        border: '1px solid #e5e7eb',
                        position: 'relative',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                        mb: 2,
                        maxWidth: `${widgetConfig.widget_width}%`
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, minHeight: 40 }}>
                            {/* Left Group: Back + Step Info */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: widgetStep > 0 ? 2 : 0 }}>
                                {widgetStep > 0 && (
                                    <Box
                                        onClick={() => setWidgetStep(widgetStep - 1)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                            color: widgetConfig.back_button_action_color || '#2563eb',
                                            fontSize: '14px',
                                            fontFamily: widgetConfig.font_family || 'var(--font-family-manrope)',
                                            fontWeight: 600,
                                        }}
                                    >
                                        <ArrowBackIosNewIcon sx={{ fontSize: '12px' }} />
                                        Back
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography sx={{
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        color: '#6b7280',
                                        fontFamily: widgetConfig.font_family || 'var(--font-family-manrope)',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1
                                    }}>
                                        Step {widgetStep + 1} / 3
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                        {[0, 1, 2].map((step) => (
                                            <Box
                                                key={step}
                                                sx={{
                                                    width: '20px',
                                                    height: '4px',
                                                    borderRadius: '2px',
                                                    backgroundColor: step <= widgetStep ? (widgetConfig.continue_button_background_color || '#2970FF') : '#e5e7eb',
                                                    transition: 'background-color 0.3s ease'
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            </Box>

                            <Tooltip
                                title={"Edit Ranking Report Widget"}
                                arrow
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            backgroundColor: '#ffffff',
                                            color: '#5e5d5dff',
                                            border: '1px solid #d8d8d8',
                                            fontSize: '12px',
                                            borderRadius: 'var(--small-border-radius)',
                                            fontWeight: 500,
                                            p: 1
                                        }
                                    },
                                    arrow: {
                                        sx: {
                                            color: '#d8d8d8'
                                        }
                                    }
                                }}
                            >
                                <IconButton
                                    onClick={() => setIsDrawerOpen(true)}
                                    sx={{
                                        textDecoration: 'none',
                                        textAlign: 'center',
                                        height: 40,
                                        fontWeight: 'normal',
                                        fontFamily: 'var(--font-family-manrope)',
                                        background: "var(--buttons-background-color)",
                                        border: "1px solid var(--buttons-border)",
                                        borderRadius: "var(--buttons-radius)",
                                        padding: "var(--buttons-padding)",
                                        color: "var(--buttons-font-color)",
                                        cursor: "pointer",
                                        fontSize: "var(--medium-body-font-size)",

                                        "&:hover": {
                                            backgroundColor: "var(--buttons-hover-background-color)",
                                            border: "var(--buttons-hover-border)",
                                        },
                                    }}
                                >
                                    <CustomIcons.PencilEdit style={{ width: '16px' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {widgetStep === 0 && (
                            <>
                                <Typography sx={{ fontFamily: widgetConfig.font_family || 'var(--font-family-manrope)', fontWeight: 600, fontSize: '15px', mb: 1, color: widgetConfig.title_text_color || '#111827' }}>
                                    Search Business
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Business name and address"
                                    size="small"
                                    InputProps={{ style: { fontSize: widgetConfig.font_size, fontFamily: widgetConfig.font_family } }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: '8px',
                                            backgroundColor: widgetConfig.text_input_background_color || '#ffffff',
                                            color: widgetConfig.text_input_text_color || '#000000',
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: widgetConfig.text_input_border_color || '#d1d5db',
                                        },
                                        "& input::placeholder": {
                                            color: widgetConfig.text_input_text_color || '#000000',
                                            opacity: 0.7
                                        },
                                        mb: 2
                                    }}
                                />
                                <Box sx={{
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '8px',
                                    // height: '180px',
                                    overflow: 'hidden',
                                    aspectRatio: '2 / 1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#9ca3af',
                                    fontFamily: 'var(--font-family-manrope)',
                                    mb: 3
                                }}>
                                    Google Map Preview
                                </Box>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disableElevation
                                    onClick={() => setWidgetStep(1)}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '16px',
                                        fontFamily: widgetConfig.font_family || 'var(--font-family-manrope)',
                                        fontWeight: 500,
                                        padding: '10px 32px',
                                        backgroundColor: widgetConfig.continue_button_background_color || '#2970FF',
                                        color: widgetConfig.continue_button_text_color || '#FFFFFF',
                                        borderRadius: '8px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            boxShadow: 'none',
                                            backgroundColor: widgetConfig.continue_button_background_color || '#2970FF',
                                            opacity: 0.9
                                        }
                                    }}
                                >
                                    {widgetConfig.continue_button_text}
                                </Button>
                            </>
                        )}

                        {widgetStep === 1 && (
                            <>
                                <Typography sx={{ fontFamily: widgetConfig.font_family || 'var(--font-family-manrope)', fontWeight: 600, fontSize: '15px', mb: 1, color: widgetConfig.title_text_color || '#111827' }}>
                                    Enter Keyword
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="e.g. plumber near me"
                                    size="small"
                                    InputProps={{ style: { fontSize: widgetConfig.font_size, fontFamily: widgetConfig.font_family } }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: '8px',
                                            backgroundColor: widgetConfig.text_input_background_color || '#ffffff',
                                            color: widgetConfig.text_input_text_color || '#000000',
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: widgetConfig.text_input_border_color || '#d1d5db',
                                        },
                                        "& input::placeholder": {
                                            color: widgetConfig.text_input_text_color || '#000000',
                                            opacity: 0.7
                                        },
                                        mb: 3
                                    }}
                                />
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disableElevation
                                    onClick={() => setWidgetStep(2)}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '16px',
                                        fontFamily: widgetConfig.font_family || 'var(--font-family-manrope)',
                                        fontWeight: 500,
                                        padding: '10px 32px',
                                        backgroundColor: widgetConfig.continue_button_background_color || '#2970FF',
                                        color: widgetConfig.continue_button_text_color || '#FFFFFF',
                                        borderRadius: '8px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            boxShadow: 'none',
                                            backgroundColor: widgetConfig.continue_button_background_color || '#2970FF',
                                            opacity: 0.9
                                        }
                                    }}
                                >
                                    {widgetConfig.continue_button_text}
                                </Button>
                            </>
                        )}

                        {widgetStep === 2 && (
                            <>
                                <Typography sx={{ fontFamily: widgetConfig.font_family || 'var(--font-family-manrope)', fontWeight: 600, fontSize: '15px', mb: 2, color: widgetConfig.title_text_color || '#111827' }}>
                                    Contact Information
                                </Typography>
                                <>
                                    {widgetConfig.contact_fields?.firstName?.enabled && <TextField InputProps={{ style: { fontSize: widgetConfig.font_size, fontFamily: widgetConfig.font_family } }} fullWidth placeholder={`First Name${widgetConfig.contact_fields.firstName.required === 'Required' ? ' *' : ''}`} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: '8px', backgroundColor: widgetConfig.text_input_background_color || '#ffffff', color: widgetConfig.text_input_text_color || '#000000' }, "& input::placeholder": { color: widgetConfig.text_input_text_color || '#000000', opacity: 0.7 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: widgetConfig.text_input_border_color || '#d1d5db' }, mb: 2 }} />}
                                    {widgetConfig.contact_fields?.lastName?.enabled && <TextField InputProps={{ style: { fontSize: widgetConfig.font_size, fontFamily: widgetConfig.font_family } }} fullWidth placeholder={`Last Name${widgetConfig.contact_fields.lastName.required === 'Required' ? ' *' : ''}`} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: '8px', backgroundColor: widgetConfig.text_input_background_color || '#ffffff', color: widgetConfig.text_input_text_color || '#000000' }, "& input::placeholder": { color: widgetConfig.text_input_text_color || '#000000', opacity: 0.7 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: widgetConfig.text_input_border_color || '#d1d5db' }, mb: 2 }} />}
                                    {widgetConfig.contact_fields?.phoneNumber?.enabled && <TextField InputProps={{ style: { fontSize: widgetConfig.font_size, fontFamily: widgetConfig.font_family } }} fullWidth placeholder={`Phone Number${widgetConfig.contact_fields.phoneNumber.required === 'Required' ? ' *' : ''}`} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: '8px', backgroundColor: widgetConfig.text_input_background_color || '#ffffff', color: widgetConfig.text_input_text_color || '#000000' }, "& input::placeholder": { color: widgetConfig.text_input_text_color || '#000000', opacity: 0.7 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: widgetConfig.text_input_border_color || '#d1d5db' }, mb: 2 }} />}
                                    <TextField InputProps={{ style: { fontSize: widgetConfig.font_size, fontFamily: widgetConfig.font_family } }} fullWidth placeholder={`Email *`} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: '8px', backgroundColor: widgetConfig.text_input_background_color || '#ffffff', color: widgetConfig.text_input_text_color || '#000000' }, "& input::placeholder": { color: widgetConfig.text_input_text_color || '#000000', opacity: 0.7 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: widgetConfig.text_input_border_color || '#d1d5db' }, mb: 2 }} />
                                    {widgetConfig.contact_fields?.website?.enabled && <TextField InputProps={{ style: { fontSize: widgetConfig.font_size, fontFamily: widgetConfig.font_family } }} fullWidth placeholder={`Website${widgetConfig.contact_fields.website.required === 'Required' ? ' *' : ''}`} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: '8px', backgroundColor: widgetConfig.text_input_background_color || '#ffffff', color: widgetConfig.text_input_text_color || '#000000' }, "& input::placeholder": { color: widgetConfig.text_input_text_color || '#000000', opacity: 0.7 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: widgetConfig.text_input_border_color || '#d1d5db' }, mb: 2 }} />}
                                    {widgetConfig.contact_fields?.companyName?.enabled && <TextField InputProps={{ style: { fontSize: widgetConfig.font_size, fontFamily: widgetConfig.font_family } }} fullWidth placeholder={`Company Name${widgetConfig.contact_fields.companyName.required === 'Required' ? ' *' : ''}`} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: '8px', backgroundColor: widgetConfig.text_input_background_color || '#ffffff', color: widgetConfig.text_input_text_color || '#000000' }, "& input::placeholder": { color: widgetConfig.text_input_text_color || '#000000', opacity: 0.7 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: widgetConfig.text_input_border_color || '#d1d5db' }, mb: 2 }} />}
                                    {widgetConfig.contact_fields?.businessName?.enabled && <TextField InputProps={{ style: { fontSize: widgetConfig.font_size, fontFamily: widgetConfig.font_family } }} fullWidth placeholder={`Business Name${widgetConfig.contact_fields.businessName.required === 'Required' ? ' *' : ''}`} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: '8px', backgroundColor: widgetConfig.text_input_background_color || '#ffffff', color: widgetConfig.text_input_text_color || '#000000' }, "& input::placeholder": { color: widgetConfig.text_input_text_color || '#000000', opacity: 0.7 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: widgetConfig.text_input_border_color || '#d1d5db' }, mb: 3 }} />}
                                </>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disableElevation
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '16px',
                                        fontFamily: widgetConfig.font_family || 'var(--font-family-manrope)',
                                        fontWeight: 500,
                                        padding: '10px 32px',
                                        backgroundColor: widgetConfig.submit_button_background_color || '#2970FF',
                                        color: widgetConfig.submit_button_text_color || '#FFFFFF',
                                        borderRadius: '8px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            boxShadow: 'none',
                                            backgroundColor: widgetConfig.submit_button_background_color || '#2970FF',
                                            opacity: 0.9
                                        }
                                    }}
                                >
                                    {widgetConfig.button_text}
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>

                {/* RIGHT SIDE */}
                <Box sx={{ flex: 1, width: '100%' }}>
                    <Box sx={{ px: 1, py: 1.4 }}>
                        <Box
                            sx={{
                                height: 500,
                                boxShadow: 'none',
                                flex: 1,
                                marginBottom: 1
                            }}
                        >
                            {widgetLoading || !widgetId ? (
                                // ── Skeleton state: widget ID not yet ready ──
                                <Box>
                                    <Skeleton variant="text" width={220} height={28} sx={{ mb: 2, borderRadius: 1 }} />
                                    <Box sx={{ pl: 2, mb: 4, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                        <Skeleton variant="text" width="75%" height={20} sx={{ borderRadius: 1 }} />
                                        <Skeleton variant="text" width="80%" height={20} sx={{ borderRadius: 1 }} />
                                        <Skeleton variant="text" width="70%" height={20} sx={{ borderRadius: 1 }} />
                                        <Skeleton variant="text" width="85%" height={20} sx={{ borderRadius: 1 }} />
                                    </Box>
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={100}
                                        sx={{ borderRadius: 'var(--medium-border-radius)', border: '1px solid #e5e7eb' }}
                                    />
                                </Box>
                            ) : (
                                // ── Loaded state: widget ID is available ──
                                <>
                                    <Typography sx={{ fontWeight: '500 !important', fontSize: 16, mb: 2 }}>How to use this widget code:</Typography>
                                    <Typography sx={{ fontFamily: 300, fontSize: 14, pl: 2, mb: 4 }}>
                                        1️⃣ Copy the below script tag.
                                        <br />
                                        2️⃣ Paste it into the &lt;body&gt; section of any website.
                                        <br />
                                        3️⃣ Save and reload the page.
                                        <br />
                                        4️⃣ The widget preview linked with this widget ID will appear automatically.
                                    </Typography>
                                    <Box
                                        sx={{
                                            fontFamily: 'Monospace',
                                            fontSize: '0.9rem',
                                            lineHeight: 1.5,
                                            borderRadius: 'var(--medium-border-radius)',
                                            border: '1px solid #d8d8d8',
                                            backgroundColor: 'rgba(0,0,0,0.03)',
                                            p: 2,
                                            overflow: 'auto',
                                            maxHeight: 320,
                                            minHeight: 100,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            position: 'relative'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                zIndex: 1
                                            }}
                                        >
                                            <Tooltip
                                                title={copied ? 'Copied' : 'Copy code'}
                                                arrow
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            backgroundColor: '#ffffff',
                                                            color: '#5e5d5dff',
                                                            border: '1px solid #d8d8d8',
                                                            fontSize: '12px',
                                                            borderRadius: 'var(--small-border-radius)',
                                                            fontWeight: 500,
                                                            p: 1
                                                        }
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: '#d8d8d8'
                                                        }
                                                    }
                                                }}
                                            >
                                                <IconButton onClick={handleCopy} aria-label="copy code" size="small">
                                                    {copied ? <CheckIcon size={18} /> : <ContentCopyIcon sx={{ fontSize: 18 }} />}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        <Typography
                                            ref={codeRef}
                                            component="pre"
                                            sx={{
                                                m: 0,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {`<script src="${API_BASE_URL?.slice(0, -4)}public/agency-widget" data-widget-id="${widgetId}" data-api-url="${API_BASE_URL}"></script>`}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>

                </Box>
            </Box>

            <div className="agency-report-table-container">

                <div style={{ padding: '10px 0px' }}>
                    <div className="dashboard-business-table-wrapper">
                        <div className="table-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <TextField
                                variant="outlined"
                                className="search-box"
                                size="small"
                                placeholder="Search leads..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: '300px',
                                }}
                            />


                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    justifyContent: 'flex-end',
                                    alignItems: 'center'
                                }}
                            >

                                <Tooltip
                                    title={`Refresh Report Widget Data`}
                                    arrow
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                backgroundColor: '#ffffff',
                                                color: '#5e5d5dff',
                                                border: '1px solid #d8d8d8',
                                                fontSize: '12px',
                                                borderRadius: 'var(--small-border-radius)',
                                                fontWeight: 500,
                                                p: 1
                                            }
                                        },
                                        arrow: {
                                            sx: {
                                                color: '#d8d8d8'
                                            }
                                        }
                                    }}
                                >
                                    <IconButton
                                        style={{
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            textAlign: 'center',
                                            height: 40,
                                            fontWeight: 'normal',
                                            fontFamily: 'var(--font-family-manrope)'
                                        }}
                                        className="export-button"
                                        onClick={() => {
                                            fetchReports()
                                        }}
                                    >
                                        <CustomIcons.Refresh style={{ width: '16px' }} />
                                    </IconButton>
                                </Tooltip>

                                <div
                                    className="export-button"
                                    style={{
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        textAlign: 'center',
                                        cursor: workspaceWidgetReportExportLoading ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    onClick={!workspaceWidgetReportExportLoading ? handleExportClick : undefined}
                                >
                                    {workspaceWidgetReportExportLoading ? (
                                        <CircularProgress size={20} sx={{ color: 'inherit' }} />
                                    ) : (
                                        'Export'
                                    )}
                                </div>

                                <CSVLink
                                    data={AGENCY_EXPORT_CSV_DATA(workspaceWidgetReportExportData)}
                                    headers={AGENCY_EXPORT_CSV_HEADER}
                                    filename={`agency-widget-${formattedDate2()}.csv`}
                                    ref={csvLinkRef}
                                    style={{ display: 'none' }}
                                />

                            </Box>

                        </div>


                        <div className="table-x-scroll">
                            <table className="business-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort('business_name')}>Business {getSortArrow('business_name')}</th>
                                        <th onClick={() => handleSort('first_name')}>Name {getSortArrow('first_name')}</th>
                                        <th onClick={() => handleSort('email')}>Email {getSortArrow('email')}</th>
                                        <th onClick={() => handleSort('keyword')}>Keyword {getSortArrow('keyword')}</th>
                                        <th onClick={() => handleSort('phone_number')}>Phone {getSortArrow('phone_number')}</th>
                                        <th onClick={() => handleSort('createdAt')}>Date {getSortArrow('createdAt')}</th>
                                        <th>Report URL</th>
                                        <th onClick={() => handleSort('report_state')}>Status {getSortArrow('report_state')}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {workspaceWidgetReportDataLoading ? (
                                        <tr>
                                            <td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
                                                Loading reports...
                                            </td>
                                        </tr>
                                    ) : sortedData.length > 0 ? (
                                        sortedData.map((report: any) => (
                                            <tr key={report._id}>
                                                <td>{report.business_name}</td>
                                                <td>{report.first_name} {report.last_name}</td>
                                                <td>{report.email}</td>
                                                <td>{report.keyword}</td>
                                                <td>{report.phone_number || '-'}</td>
                                                <td>{moment(report.createdAt).format('MMM DD, YYYY')}</td>
                                                <td>
                                                    <IconButton
                                                        disabled={!report.public_report_url}
                                                        onClick={() => window.open(report.public_report_url, '_blank')}
                                                        sx={{
                                                            color: '#4CAF50',
                                                            backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(76, 175, 80, 0.16)'
                                                            },
                                                            borderRadius: '50%',
                                                            p: 1
                                                        }}
                                                        aria-label="view invoice"
                                                    >
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </td>
                                                <td>
                                                    {(report.report_state?.toLowerCase() === 'failed' || report.report_state?.toLowerCase() === 'skipped') ? (
                                                        <Box sx={{ width: 'max-content' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start',
                                                                        gap: '6px',
                                                                        background: '#FDE2DF',
                                                                        color: '#EA4136',
                                                                        padding: '4px 10px',
                                                                        borderRadius: '8px',
                                                                        textTransform: 'capitalize',
                                                                        fontSize: '12px',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {report.report_state}
                                                                </Box>
                                                                <Button
                                                                    onClick={async (e: any) => {
                                                                        e.stopPropagation();
                                                                        const result = await dispatch(workspaceWidgetRetryScanAction(report._id));
                                                                        if (workspaceWidgetRetryScanAction.fulfilled.match(result)) {
                                                                            fetchReports();
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        margin: '0',
                                                                        textTransform: 'none',
                                                                        gap: '4px',
                                                                        fontWeight: 'inherit',
                                                                        padding: '0px',
                                                                        fontSize: '13px',
                                                                        fontFamily: 'var(--font-family-manrope)',
                                                                        minWidth: 'auto',
                                                                        '&.Mui-disabled': {
                                                                            opacity: 0.5,
                                                                            cursor: 'not-allowed'
                                                                        }
                                                                    }}
                                                                >
                                                                    <CustomIcons.Refresh style={{ width: '15px' }} />
                                                                    Retry
                                                                </Button>
                                                            </Box>

                                                            <Box
                                                                sx={{
                                                                    height: '4px',
                                                                    width: '100%',
                                                                    backgroundColor: 'rgb(255,0,0)',
                                                                    borderRadius: 'var(--small-border-radius)',
                                                                    display: 'block',
                                                                    margin: '8px 0 4px 0'
                                                                }}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'flex-start',
                                                                gap: '6px',
                                                                background: getStatusStyles(report.report_state).bg,
                                                                color: getStatusStyles(report.report_state).color,
                                                                padding: '4px 10px',
                                                                borderRadius: '8px',
                                                                textTransform: 'capitalize',
                                                                fontSize: '12px',
                                                                whiteSpace: 'nowrap',
                                                                minWidth: '100px',
                                                                width: '100px'
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    width: '8px',
                                                                    height: '8px',
                                                                    borderRadius: '50%',
                                                                    background: getStatusStyles(report.report_state).color,
                                                                    display: 'inline-block',
                                                                    flexShrink: 0
                                                                }}
                                                            />
                                                            {report.report_state}
                                                        </Box>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
                                                No reports found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="pagination--localBusiness" style={{ padding: '20px' }}>
                            {pagination.totalItems > 0 && (
                                <>
                                    <span>
                                        {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}–{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} results shown
                                    </span>

                                    <div className="page-controls--localBusiness">
                                        <Pagination
                                            count={pagination.totalPages}
                                            page={page}
                                            onChange={handlePageChange}
                                            color="primary"
                                        />
                                        <Select
                                            value={rowsPerPage}
                                            onChange={handleRowsPerPageChange}
                                            size="small"
                                            sx={{ height: '32px' }}
                                        >
                                            {[10, 20, 50].map((val) => (
                                                <MenuItem sx={menuItemSX} key={val} value={val}>
                                                    {val} / page
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Box >

    );
};

export default AgencyWidget;
